// Builds a responsive `srcset` for the committed photo sets in public/.
//
// The smaller widths are static files produced by
// scripts/gen-image-variants.mjs (which must stay in sync with VARIANT_WIDTHS).
// Native widths are recorded here so each descriptor is accurate and the browser
// never downscales a 1536px file to paint a ~270–590px box — and, for the events
// set, so it never claims a width a file does not have: those photos come out of
// the pitch deck at 194–1600px, well under the facility set, and an inflated
// descriptor would make the browser pick a soft image over a sharp one.
const VARIANT_WIDTHS = [480, 768, 1152];

const NATIVE_WIDTH: Record<string, Record<string, number>> = {
  facility: {
    "real-1": 1536, "real-2": 1024, "real-3": 1536, "real-4": 1536,
    "real-5": 1536, "real-6": 1536, "real-7": 1536, "real-8": 1536,
    "facility-1": 1080, "facility-3": 1080, "facility-4": 1080, "facility-5": 1080,
    "facility-6": 1080, "facility-7": 1080, "facility-8": 1080,
    "booking-section": 1536,
  },
  // Private-events set — see scripts/import-event-images.mjs, which prints
  // these values when it regenerates the folder.
  events: {
    hero: 1600,
    "courts-play": 847, "lounge-dog": 417, "lounge-table": 419,
    "group-courts": 847, "bar-guests": 419, "court-screens": 420,
    "photo-booth": 411, "phone-booths": 411, "lounge-crest": 830,
    tournaments: 413, coaching: 413, "team-games": 412, activations: 413,
    "matcha-cart": 343, bar: 456, "lounge-court": 456,
    "social-1": 339, "social-2": 339, "social-3": 339, "social-4": 339,
    "logo-rethink-food": 337, "logo-tech-week": 262, "logo-mckinsey": 371,
    "logo-google": 338, "logo-meta": 354, "logo-loreal": 380, "logo-rpm": 338,
    "logo-7th-street-burger": 351, "logo-mark-pickleball": 363,
    "logo-liquid-death": 194,
    decor: 354, carts: 356,
    // Not from the deck — a club photo added later for the /events form
    // block. Converted by hand at the import script's settings, so that
    // script will not reproduce it; deliberately absent from its PHOTOS.
    "paddle-dog": 1200,
  },
  // Tennibot page set. Only the hero is registered, because it is the only
  // file on that page painted at anything like a full-bleed size. The machine
  // shot, the phone cut-out and the five line icons are all rendered into
  // boxes well under 480px, so leaving them out means buildSrcSet returns
  // undefined and each falls back to a plain `src` — one request, no
  // one-candidate srcset.
  tennibot: {
    hero: 1400,
  },
};

/**
 * `/facility/real-4.webp` ->
 * `/facility/real-4-480.webp 480w, …-768.webp 768w, …-1152.webp 1152w, /facility/real-4.webp 1536w`
 *
 * Returns undefined when the file is not in a known set, or when it is smaller
 * than every variant width (most of the events photos), so the caller falls
 * back to a plain `src` rather than emitting a one-candidate srcset.
 */
function buildSrcSet(dir: string, src: string): string | undefined {
  const m = src.match(new RegExp(`^/${dir}/([a-z0-9-]+)\\.webp$`, "i"));
  if (!m) return undefined;
  const base = m[1];
  const native = NATIVE_WIDTH[dir]?.[base];
  if (!native) return undefined;
  const entries = VARIANT_WIDTHS.filter((w) => w < native).map(
    (w) => `/${dir}/${base}-${w}.webp ${w}w`,
  );
  if (entries.length === 0) return undefined;
  entries.push(`${src} ${native}w`);
  return entries.join(", ");
}

export function facilitySrcSet(src: string): string | undefined {
  return buildSrcSet("facility", src);
}

export function eventSrcSet(src: string): string | undefined {
  return buildSrcSet("events", src);
}

export function tennibotSrcSet(src: string): string | undefined {
  return buildSrcSet("tennibot", src);
}
